import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MuiCard from "@mui/material/Card";
import { Box, Button, CardHeader, Collapse, Divider, IconButton, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { InputText } from "primereact/inputtext";
import {
  EditableReusableCustomQuestionGroup,
  ReusableCustomQuestionGroup,
} from "../../../shared/interfaces/ReusableCustomQuestionGroup";
import { EditButton } from "../../../shared/FormLayout/IconButtons/EditButton";
import { useCustomQuestionGroupDelete } from "../../../data/CustomWorkflowQuestion/useCustomQuestionGroupDelete";
import { useCustomQuestionGroupUpdate } from "../../../data/CustomWorkflowQuestion/useCustomQuestionGroupUpdate";
import { ProgressButton } from "../../../shared/ProgressButton";
import { CancelButton } from "../../../shared/CancelButton";
import { ReusableCustomQuestionField } from "../../../shared/interfaces/ReusableCustomQuestionField";
import DeleteConfirmationButton from "../../../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { ReusableQuestionFieldSelectionDialog } from "../QuestionFields/ReusableQuestionFieldSelectionDialog";
import { ReusableCustomQuestionFields } from "../QuestionFields/ReusableCustomQuestionFields";

interface Props {
  customerId: number;
  customQuestionGroup: ReusableCustomQuestionGroup;
  canEditWorkflow?: boolean;
}

interface SelectModeProps {
  displayMode: "select";
  isSelected: boolean;
  onSelectionChange: (selectedQuestion: ReusableCustomQuestionGroup) => void;
}

interface EditModeProps {
  displayMode: "edit";
  onSuccess: (successMessage: string) => void;
  onError: (errorMessage: string) => void;
}

export function CustomQuestionGroupCard(props: Props & (SelectModeProps | EditModeProps)) {
  const { t } = useTranslation();
  const { customerId, displayMode, canEditWorkflow } = props;

  const [editedCustomQuestionGroup, setEditedCustomQuestionGroup] = useState(props.customQuestionGroup);
  const [editing, setEditing] = useState(false);
  const [questionSelectionDialogOpen, setQuestionSelectionDialogOpen] = useState(false);

  const customQuestionGroupUpdate = useCustomQuestionGroupUpdate({
    onSuccess: handleQuestionGroupUpdateSuccess,
    onError: handleQuestionGroupUpdateError,
  });

  const customQuestionGroupDelete = useCustomQuestionGroupDelete({
    onSuccess: handleQuestionGroupDeleteSuccess,
    onError: handleQuestionGroupDeleteError,
  });

  function handleSaveEditGroupButtonClick() {
    const editedQuestionGroup: EditableReusableCustomQuestionGroup = {
      id: editedCustomQuestionGroup.id,
      customerId: editedCustomQuestionGroup.customerId,
      groupName: editedCustomQuestionGroup.groupName,
      questionIdList: editedCustomQuestionGroup.questionList.map((question) => question.id),
    };

    customQuestionGroupUpdate.mutate(editedQuestionGroup);
  }

  function handleQuestionGroupUpdateSuccess() {
    setEditing(false);
    displayMode === "edit" &&
      props.onSuccess(
        t("customQuestionLibrary.libraryItemUpdated", {
          val: t("customQuestions.questionGroup"),
        }),
      );
  }

  function handleQuestionGroupDeleteSuccess() {
    setEditing(false);
    displayMode === "edit" &&
      props.onSuccess(
        t("customQuestionLibrary.libraryItemDeleted", {
          val: t("customQuestions.questionGroup"),
        }),
      );
  }

  function handleQuestionGroupUpdateError() {
    displayMode === "edit" &&
      props.onError(
        t("common.dataUpdateError", {
          val: t("customQuestions.questionGroup"),
        }),
      );
  }

  function handleQuestionGroupDeleteError() {
    displayMode === "edit" &&
      props.onError(
        t("common.dataDeleteError", {
          val: t("customQuestions.questionGroup"),
        }),
      );
  }

  function handleCancelEditGroupButtonClick() {
    setEditedCustomQuestionGroup(props.customQuestionGroup);
    setEditing(false);
  }

  function handleConfirmDeleteCustomQuestionClick() {
    customQuestionGroupDelete.mutate(props.customQuestionGroup.id);
  }

  function handleQuestionGroupNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEditedCustomQuestionGroup((prevState) => ({
      ...prevState,
      groupName: e.target.value,
    }));
  }

  function handleQuestionListChange(selectedQuestions: ReusableCustomQuestionField[]) {
    setEditedCustomQuestionGroup((prevState) => ({
      ...prevState,
      questionList: selectedQuestions,
    }));
  }

  return (
    <>
      <Collapse in={!customQuestionGroupDelete.isSuccess}>
        <MuiCard variant="outlined">
          <CardHeader
            title={
              <Stack direction="row" alignItems={"center"} spacing={1}>
                {displayMode === "select" && (
                  <IconButton
                    onClick={() => {
                      props.onSelectionChange(props.customQuestionGroup);
                    }}
                  >
                    {props.isSelected ? <CheckCircleIcon color={"success"} /> : <CheckCircleOutlineIcon />}
                  </IconButton>
                )}
                <Stack direction="row" alignItems="baseline" spacing={2}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("customQuestions.questionGroup")}:
                  </Typography>
                  {editing ? (
                    <InputText
                      placeholder={t("customQuestions.questionGroupName")}
                      value={editedCustomQuestionGroup.groupName}
                      onChange={handleQuestionGroupNameChange}
                    />
                  ) : (
                    <Typography variant="h6">{editedCustomQuestionGroup.groupName}</Typography>
                  )}
                </Stack>
              </Stack>
            }
            action={
              displayMode === "edit" && (
                <>
                  {editing ? (
                    <>
                      <ProgressButton
                        loading={customQuestionGroupUpdate.isLoading}
                        onClick={handleSaveEditGroupButtonClick}
                      >
                        {t("common.save")}
                      </ProgressButton>
                      <CancelButton
                        onClick={handleCancelEditGroupButtonClick}
                        disabled={customQuestionGroupUpdate.isLoading}
                      />
                    </>
                  ) : (
                    canEditWorkflow && (
                      <>
                        <EditButton onClick={() => setEditing(true)} />
                        <DeleteConfirmationButton
                          confirmationDialogHeader={t("common.deleteConfirmationShort", {
                            val: t("customQuestions.questionGroup"),
                            transform: "lowercase",
                          })}
                          confirmationDialogMessage={t("customQuestionLibrary.confirmDeleteQuestionGroup")}
                          onConfirmDeleteButtonClick={handleConfirmDeleteCustomQuestionClick}
                          variant="icon"
                        />
                      </>
                    )
                  )}
                </>
              )
            }
          />
          <Divider />
          <Stack>
            {editing && (
              <Box display="flex" justifyContent="center" m={1}>
                <Button
                  onClick={() => setQuestionSelectionDialogOpen(true)}
                  startIcon={<LibraryBooksIcon />}
                  sx={{ textTransform: "uppercase" }}
                >
                  {t("customQuestionLibrary.editQuestionList")}
                </Button>
              </Box>
            )}
            <ReusableCustomQuestionFields
              customQuestionFields={editedCustomQuestionGroup.questionList}
              customerId={customerId}
              displayMode="view"
              containerProps={{ variant: "elevation" }}
              canEditWorkflow={canEditWorkflow}
            />
          </Stack>
        </MuiCard>
      </Collapse>
      {questionSelectionDialogOpen && (
        <ReusableQuestionFieldSelectionDialog
          customerId={customerId}
          selectedQuestions={editedCustomQuestionGroup.questionList}
          onApplySelection={handleQuestionListChange}
          open={questionSelectionDialogOpen}
          onClose={() => setQuestionSelectionDialogOpen(false)}
          canEditWorkflow={canEditWorkflow}
        />
      )}
    </>
  );
}
